<script setup lang="ts">
import axios from 'axios'
import dayjs from 'dayjs'

import VueApexCharts from 'vue3-apexcharts'

import type { TabKey } from '@/components/base/BaseTab.vue'
import BaseTab from '@/components/base/BaseTab.vue'
import CampaignPreview from '@/components/CampaignPreview.vue'
import { patchContentScope, useContentScopeSave } from '@/composables/contentScope'
import type { ContentScope } from '@/types/contentScope'
import EmailTemplatePreview from '@/views/email/components/EmailTemplatePreview.vue'
import MarketingFlowPerformanceActionCard from '@/views/autopilot/Components/MarketingFlowPerformanceActionCard.vue'
import { fromApiResponse } from '@/views/email/utils/apiTransformers'
import type { Campaign, CampaignStatistics } from '@/types/types'
import CampaignTargetBlock from './CampaignPage/CampaignTargetBlock.vue'
import CampaignConvertedCustomers from './components/CampaignConvertedCustomers.vue'
import CampaignStatisticsCards from './components/CampaignStatisticsCards.vue'
import { getCampaignPreviewText } from './utils/campaignMessage'

import type { LoyaltyReward } from '@/types/types'

const notification = useNotification()
const { frequencyTypes, permissions, user, selectedStore, stores } = useLocalStorage()
const { formatNumber, formatPercentage } = useNumber()
const { t } = useI18n()
const route = useRoute()
const router = useRouter()

const storeFilter = ref<number | null>(selectedStore.value)
const currentTab = ref((route.query?.activeTab as string) || 'preview')
const tabs = ref<TabKey[]>([
  {
    value: 'preview',
    name: t('pages.marketingFlow.marketingFlowEditPage.preview')
  },
  {
    value: 'performance',
    name: t('pages.marketingFlow.marketingFlowEditPage.performance')
  }
])

const statistics = ref<CampaignStatistics>()
const convertedCustomers = ref<any[]>([])

const statisticChartOptions = ref({
  chart: {
    id: 'conversions',
    zoom: {
      enabled: false
    },
    animations: {
      enabled: true
    }
  },
  colors: ['#5b21b6'],
  plotOptions: {
    bar: {
      borderRadius: 8,
      dataLabels: {
        position: 'top'
      }
    }
  },
  dataLabels: {
    enabled: false
  },
  xaxis: {} as { categories?: string[] }
})
const statisticChartSeries = ref<{ name: string; data: (string | number)[] }[]>([])
const loaded = ref(false)
const isProcessing = ref(false)
const statisticChartDisplay = ref(false)
const campaign = ref<Campaign>()
const table = ref({
  pagination: {
    currentPage: 1,
    lastPage: 1,
    total: 0,
    pageSize: 15
  },
  columns: {
    customer: {
      id: 'phone',
      label: 'pages.campaign.campaignDetailPage.tables.thead.customer',
      type: 'customer'
    },
    checkinDate: {
      id: 'lastValidCheckinDate',
      label: 'pages.campaign.campaignDetailPage.tables.thead.convertedDatetime',
      type: 'datetime'
    },
    store: {
      label: 'pages.campaign.campaignDetailPage.tables.thead.store'
    }
  },
  rows: [] as any[]
})
interface CampaignTarget {
  store_id: string[]
  gender: string[]
  frequency_type: string[]
  age_min: number
  age_max: number
  checkin_min: number
  checkin_max: number
  point_min: number
  point_max: number
  tag_id: string[]
  contest_game_id: string[]
  survey_question_choice_id: string[]
  convert: boolean
  first_checkin: { from: string; to: string }
  last_visit: { from: string; to: string }
  segment_id: string[]
  excluded_segment_id: string[]
  [key: string]: string[] | string | number | boolean | { from: string; to: string }
}

const target = ref<CampaignTarget>({
  store_id: [],
  gender: [],
  frequency_type: [],
  age_min: 0,
  age_max: 100,
  checkin_min: 0,
  checkin_max: 5000,
  point_min: 0,
  point_max: 5000,
  tag_id: [],
  contest_game_id: [],
  survey_question_choice_id: [],
  convert: false,
  first_checkin: {
    from: '',
    to: ''
  },
  last_visit: {
    from: '',
    to: ''
  },
  segment_id: [],
  excluded_segment_id: []
})

const merchantSmsName = ref('')

const availableFilterStores = computed(() => {
  const campaignStoreIds = campaign.value?.stores.map((s) => s.id) ?? []
  return stores.value.filter((store) => campaignStoreIds.includes(store.id))
})

const mockupMessage = computed(() => {
  return getCampaignPreviewText(
    campaign.value?.messageContent ?? '',
    campaign.value?.stores[0].name ?? '',
    campaign.value?.reward as unknown as LoyaltyReward
  )
})

const senderName = computed(() => {
  if (campaign.value?.isStoreSenderName === true) {
    return campaign.value?.stores[0].smsName
  } else {
    return merchantSmsName.value
  }
})

const frequency = computed(() => {
  return frequencyTypes.value as { id: number; slug: string; name: string }[]
})

const isEmailCampaign = computed(() => campaign.value?.type === 'email')

const { listDomains } = useDomainConfig()
const { getEmailContent } = useEmailContent()
const emailFrom = ref('')
const emailSubject = ref('')
const emailPreviewText = ref('')

const emailStatistics = computed(() => {
  if (!isEmailCampaign.value || !statistics.value) return { left: [], right: [] }

  const s = statistics.value
  const sent = s.emailSent ?? s.messagesCount ?? 0
  const delivered = s.emailDelivered ?? 0

  const deliveredPct = sent > 0 ? (delivered / sent) * 100 : 0
  const openedPct = delivered > 0 ? ((s.emailOpenedUnique ?? 0) / delivered) * 100 : 0
  const clickedPct = delivered > 0 ? ((s.emailClicked ?? 0) / delivered) * 100 : 0
  const uniqueClickedPct = delivered > 0 ? ((s.emailClickedUnique ?? 0) / delivered) * 100 : 0
  const bouncedPct = sent > 0 ? ((s.emailBounced ?? 0) / sent) * 100 : 0
  const softBouncedPct = sent > 0 ? ((s.emailSoftBounced ?? 0) / sent) * 100 : 0
  const hardBouncedPct = sent > 0 ? ((s.emailHardBounced ?? 0) / sent) * 100 : 0
  const spamPct = sent > 0 ? ((s.emailSpamComplaints ?? 0) / sent) * 100 : 0
  const unsubscribedPct = sent > 0 ? ((s.emailUnsubscribed ?? 0) / sent) * 100 : 0

  return {
    left: [
      {
        label: t('pages.marketingFlow.marketingFlowPerformance.statistics.emailSent'),
        value: formatNumber(sent, false),
        percentage: undefined,
        tooltip: t('pages.marketingFlow.marketingFlowPerformance.statistics.tooltips.emailSent')
      },
      {
        label: t('pages.marketingFlow.marketingFlowPerformance.statistics.emailReceived'),
        value: formatNumber(delivered, false),
        percentage: formatPercentage(deliveredPct, 1),
        tooltip: t('pages.marketingFlow.marketingFlowPerformance.statistics.tooltips.emailReceived')
      },
      {
        label: t('pages.marketingFlow.marketingFlowPerformance.statistics.emailOpened'),
        value: formatNumber(s.emailOpenedUnique ?? 0, false),
        percentage: formatPercentage(openedPct, 1),
        tooltip: t('pages.marketingFlow.marketingFlowPerformance.statistics.tooltips.emailOpened')
      },
      {
        label: t('pages.marketingFlow.marketingFlowPerformance.statistics.emailLinksClicked'),
        value: formatNumber(s.emailClicked ?? 0, false),
        percentage: formatPercentage(clickedPct, 1),
        tooltip: t('pages.marketingFlow.marketingFlowPerformance.statistics.tooltips.emailLinksClicked')
      },
      {
        label: t('pages.marketingFlow.marketingFlowPerformance.statistics.emailUniqueClicks'),
        value: formatNumber(s.emailClickedUnique ?? 0, false),
        percentage: formatPercentage(uniqueClickedPct, 1),
        tooltip: t('pages.marketingFlow.marketingFlowPerformance.statistics.tooltips.emailUniqueClicks')
      }
    ],
    right: [
      {
        label: t('pages.marketingFlow.marketingFlowPerformance.statistics.emailBounced'),
        value: formatNumber(s.emailBounced ?? 0, false),
        percentage: formatPercentage(bouncedPct, 1),
        tooltip: t('pages.marketingFlow.marketingFlowPerformance.statistics.tooltips.emailBounced')
      },
      {
        label: t('pages.marketingFlow.marketingFlowPerformance.statistics.emailSoftBounced'),
        value: formatNumber(s.emailSoftBounced ?? 0, false),
        percentage: formatPercentage(softBouncedPct, 1),
        tooltip: t('pages.marketingFlow.marketingFlowPerformance.statistics.tooltips.emailSoftBounced')
      },
      {
        label: t('pages.marketingFlow.marketingFlowPerformance.statistics.emailHardBounced'),
        value: formatNumber(s.emailHardBounced ?? 0, false),
        percentage: formatPercentage(hardBouncedPct, 1),
        tooltip: t('pages.marketingFlow.marketingFlowPerformance.statistics.tooltips.emailHardBounced')
      },
      {
        label: t('pages.marketingFlow.marketingFlowPerformance.statistics.emailSpam'),
        value: formatNumber(s.emailSpamComplaints ?? 0, false),
        percentage: formatPercentage(spamPct, 1),
        tooltip: t('pages.marketingFlow.marketingFlowPerformance.statistics.tooltips.emailSpam')
      },
      {
        label: t('pages.marketingFlow.marketingFlowPerformance.statistics.emailUnsubscribed'),
        value: formatNumber(s.emailUnsubscribed ?? 0, false),
        percentage: formatPercentage(unsubscribedPct, 1),
        tooltip: t('pages.marketingFlow.marketingFlowPerformance.statistics.tooltips.emailUnsubscribed')
      }
    ]
  }
})

const parent = computed(() =>
  isEmailCampaign.value
    ? { label: t('pages.campaignEmail.title'), to: { name: 'CampaignEmailListPage' } }
    : {
        label: t(`pages.campaign.campaignPage.${campaign.value?.type}.title`),
        to: { name: 'CampaignList', query: { type: campaign.value?.type } }
      }
)

const showCampaignDeleteButton = computed(() => (permissions.value as Record<string, boolean>)['campaign.update'])

const changePage = (page: number) => {
  table.value.pagination.currentPage = page
  fetchConvertedCustomers()
}

const fetchConvertedCustomers = () => {
  return axios
    .get(`/campaigns/${route.params.campaignId}/converted-customers`, {
      params: {
        pageSize: 15,
        page: table.value.pagination.currentPage,
        ...(storeFilter.value && { storeIds: [storeFilter.value] })
      }
    })
    .then((response) => {
      table.value.pagination = {
        currentPage: response.data.meta.current_page,
        lastPage: response.data.meta.last_page,
        total: response.data.meta.total,
        pageSize: response.data.meta.per_page
      }
      convertedCustomers.value = response.data.data
    })
    .catch(() => {
      notification.create('error', {
        text: t('pages.campaign.campaignDetailPage.notifications.convertedCustomersError'),
        timeout: 5000,
        persist: false
      })
    })
}

const fetchEmailMetadata = async () => {
  if (!isEmailCampaign.value || !campaign.value?.emailContentId) return

  try {
    const [domains, emailData] = await Promise.all([
      listDomains(),
      getEmailContent(String(campaign.value.emailContentId))
    ])

    const readyDomain = domains.find((d) => d.is_ready_for_sending)
    if (readyDomain) {
      emailFrom.value = `noreply@${readyDomain.domain}`
    }

    const parsed = fromApiResponse(emailData)
    emailSubject.value = parsed.subject
    emailPreviewText.value = parsed.previewText
  } catch {
    // silently fail
  }
}

const fetchCampaign = () => {
  return axios.get(`/campaigns/${route.params.campaignId}`).then((response) => {
    campaign.value = response.data.data
    fetchStatistics()
    fetchEmailMetadata()

    const targets = response.data.data.targets.filter((t: Record<string, any>) => t.selectorValue !== 'all')

    const selectorWithSingleValues = ['age_min', 'age_max', 'checkin_min', 'checkin_max', 'point_min', 'point_max']
    const selectorDates = ['first_checkin_from', 'first_checkin_to', 'last_visit_from', 'last_visit_to']

    targets.forEach((targetData: Record<string, any>) => {
      if (targetData.selectorKey === 'convert') {
        return (target.value[targetData.selectorKey] = targetData.selectorValue === '1')
      }
      if (selectorDates.includes(targetData.selectorKey)) {
        const dateValue = new Date(targetData.selectorValue).toLocaleDateString('fr-FR')
        switch (targetData.selectorKey) {
          case 'first_checkin_from':
            target.value.first_checkin.from = dateValue
            break
          case 'first_checkin_to':
            target.value.first_checkin.to = dateValue
            break
          case 'last_visit_from':
            target.value.last_visit.from = dateValue
            break
          case 'last_visit_to':
            target.value.last_visit.to = dateValue
            break
        }
        return
      }
      if (selectorWithSingleValues.includes(targetData.selectorKey)) {
        target.value[targetData.selectorKey] = targetData.selectorValue
      } else {
        let value = targetData.selectorValue
        const selectorToRedefine = [
          'frequency_type',
          'gender',
          'store_id',
          'contest_game_id',
          'tag_id',
          'survey_question_choice_id',
          'first_checkin_from',
          'first_checkin_to',
          'last_visit_from',
          'last_visit_to',
          'segment_id',
          'excluded_segment_id'
        ]

        if (selectorToRedefine.includes(targetData.selectorKey)) {
          let frequencyFinder
          switch (targetData.selectorKey) {
            case 'frequency_type':
              // We use f.id === target.selectorValue || f.slug === target.selectorValue because of target.selectorValue value can be changed
              frequencyFinder = frequency.value.find(
                (f) => f.id == targetData.selectorValue || f.slug === targetData.selectorValue
              )
              value = frequencyFinder?.name ?? t('pages.campaign.campaignTargetBlock.allTypes')
              break
            case 'gender':
              value = t(`global.gender.${targetData.selectorValue}`)
              break
            case 'contest_game_id':
              value = targetData.contestGame.name
              break
            case 'store_id':
              // Distinct stores, because it can come from campaign_stores or campaign_targets, but have to be merged
              if (!target.value['store_id'].includes(targetData.store?.name)) {
                value = targetData.store?.name
              } else {
                value = null
              }
              break
            case 'tag_id':
              value = targetData.tag?.name ?? targetData.selectorValue ?? null
              break
            case 'survey_question_choice_id':
              value = `${targetData.surveyChoice.question.questionText} : ${targetData.surveyChoice.text}`
              break
            case 'segment_id':
              value = targetData.segment?.name
              break
            case 'excluded_segment_id':
              value = targetData.excludedSegment?.name
              break
          }
        }
        if (value !== null) {
          const arr = target.value[targetData.selectorKey]
          if (Array.isArray(arr)) {
            arr.push(value)
          }
        }
      }
    })
  })
}

const launchDate = (format: string) => {
  return dayjs(campaign.value?.sentAt).format(format)
}

const getSendersName = () => {
  return axios.get(`/merchants/${user.value.merchant.id}`).then((response) => {
    merchantSmsName.value = response.data.data.smsName
  })
}

const fetchVisits = () => {
  const date = new Date(campaign.value?.sentAt ?? '')
  const fromDate = new Date(date)
  const toDate = new Date(date.setDate(date.getDate() + (campaign.value?.conversionDelay ?? 0)))
  return axios
    .get(`/campaigns/${route.params.campaignId}/visits`, {
      params: {
        fromDate: fromDate,
        toDate: toDate,
        ...(storeFilter.value && { storeIds: [storeFilter.value] })
      }
    })
    .then((response) => {
      const visits = response.data.data.filter((visit: Record<string, any>) => {
        return null !== visit.checkinCount
      })

      const dates: string[] = []
      const countConversions: (string | number)[] = []
      response.data.data.forEach((visit: Record<string, any>) => {
        const date = visit.date
        dates.push(dayjs(date).format('LL'))
        const conversions = visits.filter((v: Record<string, any>) => {
          return v.date == dayjs(date).format('YYYY-MM-DD')
        })
        if (conversions[0]) {
          countConversions.push(formatNumber(conversions[0].checkinCount))
        } else {
          countConversions.push(0)
        }
      })
      statisticChartOptions.value = {
        ...statisticChartOptions.value,
        ...{
          xaxis: {
            categories: dates
          }
        }
      }
      statisticChartSeries.value = [
        {
          name: 'Conversions',
          data: countConversions
        }
      ]
      statisticChartDisplay.value = true
    })
}

const fetchStatistics = async () => {
  try {
    const { data } = await axios.get(`/campaigns/${route.params.campaignId}/statistics`, {
      params: {
        ...(storeFilter.value && { storeIds: [storeFilter.value] })
      }
    })
    statistics.value = data.data
  } catch {
    statistics.value = undefined
    notification.create('error', {
      text: t('pages.campaign.campaignDetailPage.notifications.statisticsError'),
      timeout: 5000,
      persist: false
    })
  }
}

const fetchData = async () => {
  await Promise.all([fetchCampaign(), fetchConvertedCustomers(), getSendersName()])
  await fetchVisits()
  loaded.value = true
}

const refreshStats = async () => {
  await Promise.all([fetchStatistics(), fetchConvertedCustomers()])
  await fetchVisits()
  loaded.value = true
}

const isLaunched = (date: string | undefined) => {
  return dayjs(date).isBefore(dayjs()) || dayjs(date).isSame(dayjs())
}

const deleteCampaign = () => {
  if (isProcessing.value) {
    return
  }
  isProcessing.value = true
  axios
    .delete(`/campaigns/${route.params.campaignId}`)
    .then(() => {
      notification.create('success', {
        text: t('pages.campaign.campaignDetailPage.notifications.deleteCampaign.success')
      })
      if (isEmailCampaign.value) {
        router.push({ name: 'CampaignEmailListPage' })
      } else {
        router.push({ name: 'CampaignList', query: { type: campaign.value?.type } })
      }
    })
    .catch((error: any) => {
      if (error.response?.status === 403) {
        notification.create('error', {
          text: t('pages.campaign.campaignDetailPage.notifications.deleteCampaign.error.403'),
          timeout: 2000
        })
      } else {
        notification.create('error', {
          text: t('pages.campaign.campaignDetailPage.notifications.deleteCampaign.error.500'),
          timeout: 5000
        })
      }
      isProcessing.value = false
    })
    .finally(() => (isProcessing.value = false))
}

const saveScope = useContentScopeSave()

const handleUpdateScope = async (scope: ContentScope) => {
  const current = campaign.value
  if (!current) return

  current.scope = await saveScope(() => patchContentScope('campaigns', current.id, scope))
}

const changeStore = async (store: number) => {
  storeFilter.value = store === 0 ? null : store
  await refreshStats()
}

onMounted(() => {
  fetchData()
})
</script>

<template>
  <div>
    <div v-if="loaded && campaign">
      <div class="bg-white">
        <TheDetailHeader :title="campaign.name" :parent="parent">
          <template #badge>
            <ContentScopeDetailBadge :scope="campaign.scope" subject="campaign" :save="handleUpdateScope" />
          </template>
          <template #actions>
            <AppDownloadCsvButton
              v-if="isEmailCampaign && campaign.isSent"
              :path="`campaigns/${campaign.id}/statistics/export`"
              :params="storeFilter ? { storeIds: [storeFilter] } : {}"
              is-batch
            />
            <div v-if="!campaign.isSent && showCampaignDeleteButton" class="flex items-center gap-2">
              <BaseConfirmButton
                :title="t('pages.campaign.campaignDetailPage.delete.text')"
                :text="t('pages.campaign.campaignDetailPage.delete.description')"
                :confirm-button-label="t('pages.campaign.campaignCreatePage.confirm.modal.confirm')"
                :cancel-button-label="t('pages.campaign.campaignCreatePage.confirm.modal.cancel')"
                :white="false"
                :has-name="true"
                @confirm="deleteCampaign"
              >
                <div class="flex items-center gap-3">
                  <Spinner v-if="isProcessing" class="text-white" />
                  <p>
                    {{
                      isProcessing
                        ? t('pages.campaign.campaignDetailPage.buttons.deleteOnGoing')
                        : t('pages.campaign.campaignDetailPage.buttons.deleteCampaign')
                    }}
                  </p>
                </div>
              </BaseConfirmButton>
            </div>
          </template>
        </TheDetailHeader>

        <BaseTab v-model="currentTab" :tabs="tabs" is-full-width />
      </div>

      <div class="px-6 mb-6 mt-6 flex items-center md:mt-8 sm:mb-8 sm:px-12">
        <div class="flex-1 flex flex-row items-center">
          <BaseIcon name="IconoirFilterList" class="h-6 w-6 text-foreground" />

          <div class="item-center flex-row px-3 text-foreground">
            <span>{{ t('global.filters.filter') }}</span>
          </div>

          <DropdownStore :stores-list="availableFilterStores" @update-dropdown="changeStore" />
        </div>

        <div class="flex items-center gap-2 text-blue-600">
          <BaseIcon name="HeroArrowPathIconSolid" class="h-5 w-5" />

          <span class="text-sm">
            {{ t('global.stats.upgradeAt') }}
          </span>
        </div>
      </div>

      <div v-if="currentTab === 'preview'" class="px-6 pb-6 sm:px-12 sm:pb-12 md:gap-6 xl:grid xl:grid-cols-3">
        <div class="md:col-span-2 md:mt-0">
          <Card class="h-full">
            <CardHeader>
              <CardTitle>{{ t('pages.campaign.campaignDetailPage.blocks.details.title') }}</CardTitle>
            </CardHeader>

            <CardContent>
              <dl class="grid grid-cols-1 gap-x-4 gap-y-8 sm:grid-cols-2">
                <div class="sm:col-span-1">
                  <dt class="text-xs font-medium text-muted-foreground sm:text-sm">
                    {{ t('pages.campaign.campaignDetailPage.cards.sentDate') }}
                  </dt>
                  <dd class="mt-1 text-xs text-foreground sm:text-sm">
                    {{ launchDate('D MMMM YYYY HH:mm') }}
                  </dd>
                </div>
                <div class="sm:col-span-1">
                  <p class="text-xs font-medium text-muted-foreground sm:text-sm mb-2">
                    {{ t('pages.campaign.campaignDetailPage.blocks.details.status') }}
                  </p>
                  <BaseChip
                    :color="campaign.sentAt ? 'green' : 'yellow'"
                    :text="
                      isLaunched(campaign.sentAt)
                        ? t('pages.campaign.campaignPage.items.status.sent')
                        : t('pages.campaign.campaignPage.items.status.scheduled')
                    "
                  />
                </div>

                <div class="sm:col-span-1">
                  <dt class="text-xs font-medium text-muted-foreground sm:text-sm">
                    {{ t('pages.campaign.campaignCreatePage.block.conversionDelay.title') }}
                  </dt>
                  <dd class="mt-1 text-xs text-foreground sm:text-sm">
                    {{ campaign.conversionDelay }} {{ t('global.words.days') }}
                  </dd>
                </div>

                <div v-if="campaign.type === 'sms'" class="sm:col-span-1">
                  <p class="text-xs font-medium text-muted-foreground sm:text-sm mb-1">
                    {{ t('pages.campaign.campaignCreatePage.block.qrCodeBlock.title') }}
                  </p>
                  <BaseChip
                    :color="campaign.hasQrcode ? 'green' : 'orange'"
                    :text="
                      t(
                        `pages.campaign.campaignCreatePage.block.qrCodeBlock.${
                          campaign.hasQrcode ? 'switchActivated' : 'switchDesactivated'
                        }`
                      )
                    "
                  />
                </div>

                <div v-if="!isEmailCampaign" class="sm:col-span-1">
                  <p class="text-xs font-medium text-muted-foreground sm:text-sm mb-2">
                    {{ t('pages.campaign.campaignDetailPage.blocks.details.billingType') }}
                  </p>
                  <BaseChip
                    :text="
                      campaign.client
                        ? t('pages.campaign.campaignDetailPage.blocks.details.billingTypeValues.specific')
                        : t('pages.campaign.campaignDetailPage.blocks.details.billingTypeValues.each')
                    "
                  />
                </div>

                <div v-if="campaign.client && !isEmailCampaign" class="sm:col-span-1">
                  <p class="text-xs font-medium text-muted-foreground sm:text-sm mb-2">
                    {{ t('pages.campaign.campaignDetailPage.blocks.details.client') }}
                  </p>
                  <BaseChip :text="campaign.client.name" />
                </div>

                <div v-if="campaign.reward" class="sm:col-span-1">
                  <dt class="text-xs font-medium text-muted-foreground sm:text-sm">
                    {{ t('pages.campaign.campaignDetailPage.blocks.details.reward') }}
                  </dt>
                  <dd class="mt-1 text-xs text-foreground sm:text-sm">
                    <router-link
                      :to="{ name: 'RewardDetail', params: { rewardId: campaign.reward.id } }"
                      class="flex items-center gap-3 cursor-pointer px-2 py-1"
                      style="text-decoration: none"
                    >
                      <img
                        v-if="campaign.reward.pictureUrl"
                        :src="campaign.reward.pictureUrl"
                        :alt="campaign.reward.name"
                        class="h-8 w-8 rounded-full object-cover border-2 border-primary/15 shadow-sm"
                      />
                      <div v-else class="h-8 w-8 flex items-center justify-center rounded-full bg-primary/5">
                        <BaseIcon name="HeroGiftIconSolid" class="h-5 w-5 text-primary" />
                      </div>
                      <span class="text-sm font-medium text-foreground">
                        {{ campaign.reward.name }}
                      </span>
                    </router-link>
                  </dd>
                </div>

                <div class="sm:col-span-2">
                  <p class="text-xs font-medium text-muted-foreground sm:text-sm mb-2">
                    {{ t('pages.campaign.campaignDetailPage.blocks.details.recipients') }}
                  </p>
                  <div class="mt-1 text-xs text-foreground sm:text-sm">
                    <div v-if="isEmailCampaign" class="flex items-center flex-wrap gap-2">
                      <BaseChip v-for="store in campaign.stores" :key="store.id" :text="store.name" />
                    </div>
                    <CampaignTargetBlock
                      v-else
                      :all-stores="target['store_id'].length == 0"
                      :stores="campaign.stores"
                      :all-frequency-types="target.frequency_type.length == 0"
                      :frequency-types="target.frequency_type.join(', ')"
                      :all-genders="target.gender.length == 0"
                      :genders="target.gender.join(', ')"
                      :ages="[target.age_min, target.age_max]"
                      :checkins="[target.checkin_min, target.checkin_max]"
                      :points="[target.point_min, target.point_max]"
                      :all-tags="target.tag_id.length == 0"
                      :tags="target.tag_id.join(', ')"
                      :all-surveys="target.survey_question_choice_id.length == 0"
                      :all-contest-games="target.contest_game_id.length == 0"
                      :first-checkin="target.first_checkin"
                      :last-visit="target.last_visit"
                      :segment-list="target.segment_id"
                      :segment-excluded-list="target.excluded_segment_id"
                      :surveys="target.survey_question_choice_id"
                      :contest-games="target.contest_game_id"
                    />
                  </div>
                </div>
              </dl>
              <BaseTip
                v-if="campaign.isStoreSenderName === true"
                severity="success"
                :text="t('pages.campaign.campaignDetailPage.blocks.message.info')"
              />
            </CardContent>
          </Card>
        </div>

        <div class="md:col-span-1">
          <Card class="h-full overflow-hidden pb-0">
            <CardHeader>
              <CardTitle>
                {{
                  isEmailCampaign
                    ? t('pages.campaignEmail.detail.emailPreview')
                    : t('pages.campaign.campaignDetailPage.blocks.message.title')
                }}
              </CardTitle>
            </CardHeader>

            <template v-if="isEmailCampaign">
              <div class="flex flex-col gap-3 border-b border-border p-6">
                <div class="flex items-center gap-4">
                  <span class="w-14 text-sm font-medium text-muted-foreground">
                    {{ t('pages.email.emailTemplateCreatePage.from') }}
                  </span>
                  <span class="text-sm text-primary">{{ emailFrom || '-' }}</span>
                </div>
                <div class="flex items-center gap-4">
                  <span class="w-14 text-sm font-medium text-muted-foreground">
                    {{ t('pages.email.emailTemplateCreatePage.subject') }}
                  </span>
                  <span class="text-sm text-foreground">{{ emailSubject || '-' }}</span>
                </div>
                <div class="flex items-center gap-4">
                  <span class="w-14 text-sm font-medium text-muted-foreground">
                    {{ t('pages.email.emailTemplateCreatePage.previewText') }}
                  </span>
                  <span class="text-sm text-foreground-secondary">{{ emailPreviewText || '-' }}</span>
                </div>
              </div>
              <div v-if="campaign.emailContentId" class="flex-1 overflow-hidden">
                <EmailTemplatePreview :template-id="String(campaign.emailContentId)" />
              </div>
            </template>

            <template v-else>
              <CampaignPreview
                :name="senderName"
                :message="mockupMessage"
                :raw-message="campaign.messageContent"
                :type="campaign.type"
                :reward-name="campaign.reward?.name"
                fit-content
                class="mt-auto"
              />
            </template>
          </Card>
        </div>

        <div class="md:col-span-3">
          <div v-if="convertedCustomers.length > 0">
            <CampaignConvertedCustomers
              :converted-customers="convertedCustomers"
              :pagination="table.pagination"
              @update:change-page="changePage"
            />
          </div>
          <Card v-else>
            <CardContent class="flex items-center justify-center py-6">
              <div class="text-center">
                <img
                  src="/images/avatars/woman01.jpg"
                  class="m-6 inline-block h-28 w-28 rounded-full border-8 border-primary/15"
                />
                <h3 class="text-lg font-semibold text-foreground">
                  {{ t('pages.campaign.campaignDetailPage.blocks.customerConverted.noCustomer.title') }}
                </h3>
                <p class="text-sm text-foreground">
                  {{ t('pages.campaign.campaignDetailPage.blocks.customerConverted.noCustomer.description') }}
                </p>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      <div v-if="currentTab === 'performance'" class="px-6 pb-6 sm:px-12 sm:pb-12 md:gap-6">
        <CampaignStatisticsCards
          v-if="statistics"
          :statistics="statistics"
          :has-qrcode="campaign.hasQrcode"
          :has-reward="campaign.reward !== undefined"
          :is-email-campaign="isEmailCampaign"
        />

        <MarketingFlowPerformanceActionCard
          v-if="isEmailCampaign && statistics"
          icon="HeroEnvelopeIconOutline"
          title="Email"
          :stats="emailStatistics"
        />

        <Card v-if="!isEmailCampaign" class="mt-5 overflow-hidden">
          <CardHeader>
            <CardTitle>
              {{
                t('pages.campaign.campaignDetailPage.blocks.conversions', {
                  conversionDelay: campaign.conversionDelay
                })
              }}
            </CardTitle>
          </CardHeader>

          <CardContent>
            <vue-apex-charts
              v-if="statisticChartDisplay"
              ref="statisticChartRef"
              height="400"
              type="bar"
              :options="statisticChartOptions"
              :series="statisticChartSeries"
            />
          </CardContent>
        </Card>
      </div>
    </div>
    <TheLoadingPage v-else />
  </div>
</template>
